// Fixed-timestep game loop with render interpolation (plan §4.3 / §9-B).
// Why fixed: variable dt makes movement frame-rate dependent (the 144Hz bug) and
// physics non-deterministic. We accumulate real time and step the sim at a constant
// 1/60s, then interpolate the render between the last two sim states.

export interface LoopCallbacks {
  /** Advance the simulation by a fixed dt (seconds). */
  update(dt: number): void;
  /** Draw, interpolating between previous and current sim state. alpha in [0, 1). */
  render(alpha: number): void;
}

const FIXED_DT = 1 / 60;
const MAX_FRAME = 0.25; // clamp huge gaps (tab return / GC) - Fiedler's value
const MAX_STEPS = 240; // spiral-of-death guard

export class GameLoop {
  private acc = 0;
  private last: number | null = null;
  private rafId = 0;
  private running = false;
  /** When true, sim is frozen but rendering continues (level-up draft, menus). */
  paused = false;

  constructor(private readonly cb: LoopCallbacks) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = null;
    document.addEventListener('visibilitychange', this.onVisibility);
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  // On returning to a hidden tab, discard the stale gap so we don't fast-forward.
  private onVisibility = (): void => {
    if (document.visibilityState === 'visible') this.last = null;
  };

  private tick = (tsMs: number): void => {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.tick);

    const now = tsMs / 1000; // rAF timestamp is monotonic - never use Date.now()
    if (this.last !== null && !this.paused) {
      let frame = now - this.last;
      if (frame > MAX_FRAME) frame = MAX_FRAME;
      this.acc += frame;

      let steps = 0;
      while (this.acc >= FIXED_DT) {
        this.cb.update(FIXED_DT);
        this.acc -= FIXED_DT;
        if (++steps >= MAX_STEPS) {
          this.acc = 0;
          break;
        }
      }
    }
    this.last = now;

    const alpha = this.paused ? 1 : this.acc / FIXED_DT;
    this.cb.render(alpha);
  };
}
